import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "Shared/util/custom-hooks";
import {
    AffiliatedBankResource,
    AuthenticationFlowResource,
    AuthorizationMethodResource,
    BankResource,
    ConsentResource,
    Nothing,
    PaymentInformationMessageResource,
} from "Shared/util/types";
import Sidebar from "Shared/components/sidebar";
import Button from "Shared/components/button";
import styles from "./open-banking-accounts-sidebar.css";
import { PayoutInformationStatusMessage } from "./payout-information-status-message";
import {
    SelectBankIdMethodTabData,
    RedirectTabData,
    DecoupledOtherDeviceTabData,
    DecoupledSameDeviceTabData,
    SelectBankTab,
    SelectBankIdMethodTab,
    DecoupledOtherDeviceTab,
    DecoupledSameDeviceTab,
    RedirectTab,
} from "./tabs";
import { OpenBankingAccountsSidebarProps, Step } from "./types";

export const OpenBankingAccountsSidebar = ({
    affiliatedBankId: preselectedAffiliatedBankId = null,
    bic: preselectedBic = null,
    onCancel,
    onUserVerify,
    visible,
}: OpenBankingAccountsSidebarProps) => {
    const history = useHistory();
    const location = useLocation();
    const t = useTranslation((x) => x.edit_payment_information);
    const sidebarRef = useRef<HTMLDivElement>();

    const [selectedBic, setSelectedBic] = useState<string | null>(null);
    const [step, setStep] = useState<Step>(Step.SelectBank);

    const [selectBankIdMethodTabData, setSelectBankIdMethodTabData] =
        useState<SelectBankIdMethodTabData>({
            affiliatedBankId: undefined,
            bic: "",
            finaBankId: 0,
            socialSecurityNumber: undefined,
        });

    const [redirectTabData, setRedirectTabData] = useState<RedirectTabData>({
        bic: "",
        callbackActivationCode: "",
        callbackToken: "",
        finaBankId: 0,
        isCallback: false,
        socialSecurityNumber: undefined,
    });

    const [decoupledOtherDeviceTabData, setDecoupledOtherDeviceTabData] =
        useState<DecoupledOtherDeviceTabData>({
            token: "",
            qrCodeImageUrl: "",
        });

    const [decoupledSameDeviceTabData, setDecoupledSameDeviceTabData] =
        useState<DecoupledSameDeviceTabData>({
            token: "",
            bankIdApplicationUrl: "",
        });

    useEffect(() => {
        if (!visible) {
            resetToDefault();
        } else {
            const redirectActivationCode = new URLSearchParams(
                location.search,
            ).get("code");
            const redirectCallbackToken = new URLSearchParams(
                location.search,
            ).get("state");
            const decoupledSameDeviceCallbackToken = new URLSearchParams(
                location.search,
            ).get("callbacktoken");

            if (redirectActivationCode && redirectCallbackToken) {
                history.replace(location.pathname, null);
                setRedirectTabData((prev) => ({
                    ...prev,
                    isCallback: true,
                    callbackActivationCode: redirectActivationCode,
                    callbackToken: redirectCallbackToken,
                }));
                setStep(Step.Redirect);
            } else if (decoupledSameDeviceCallbackToken) {
                setDecoupledSameDeviceTabData((prev) => ({
                    ...prev,
                    token: decoupledSameDeviceCallbackToken,
                }));
                setStep(Step.DecoupledSameDevice);
            }
        }
    }, [visible]);

    useEffect(() => {
        if (sidebarRef && sidebarRef.current) {
            sidebarRef.current.scrollTop = 0;
        }
    }, [step]);

    const handleBankChanged = useCallback((bic: string) => {
        setStatusMessage(null);
        setSelectedBic(bic);
    }, []);

    const handleBankSelected = (
        bank: BankResource,
        affiliatedBank: AffiliatedBankResource | undefined,
        socialSecurityNumber: string | undefined,
    ) => {
        setStatusMessage(null);

        const {
            authorizationMethod,
            bic,
            finaBankId: bankFinaBankId,
        } = bank || {};

        const { finaBankId: affiliatedBankFinaBankId, id: affiliatedBankId } =
            affiliatedBank || {};

        const finaBankId = affiliatedBankFinaBankId || bankFinaBankId;

        if (authorizationMethod === AuthorizationMethodResource.Decoupled) {
            setSelectBankIdMethodTabData({
                affiliatedBankId,
                bic,
                finaBankId,
                socialSecurityNumber,
            });
            setStep(Step.SelectBankIdMethod);
        } else if (
            authorizationMethod === AuthorizationMethodResource.Redirect
        ) {
            setRedirectTabData({
                callbackActivationCode: "",
                callbackToken: "",
                bic,
                finaBankId,
                isCallback: false,
                socialSecurityNumber,
            });
            setStep(Step.Redirect);
        } else {
            throw new Error(
                `Unsupported AuthorizationMethodResource: '${authorizationMethod}'`,
            );
        }
    };

    const handleBankIdMethodSelected = useCallback(
        (
            authenticationFlow: AuthenticationFlowResource,
            consentResource: ConsentResource,
        ) => {
            if (
                authenticationFlow ===
                AuthenticationFlowResource.DecoupledOtherDevice
            ) {
                setDecoupledOtherDeviceTabData({
                    token: consentResource.token,
                    qrCodeImageUrl: consentResource.challengeData,
                });
                setStep(Step.DecoupledOtherDevice);
            } else if (
                authenticationFlow ===
                AuthenticationFlowResource.DecoupledSameDevice
            ) {
                setDecoupledSameDeviceTabData({
                    token: consentResource.token,
                    bankIdApplicationUrl: consentResource.challengeData,
                });
                setStep(Step.DecoupledSameDevice);
            } else {
                throw new Error(
                    `Unsupported AuthenticationFlowResource value: '${authenticationFlow}'`,
                );
            }
        },
        [],
    );

    const handleChangeBankIdMethod = useCallback(
        () => setStep(Step.SelectBankIdMethod),
        [],
    );

    const handleUserVerified = useCallback(() => {
        onUserVerify();
    }, [onUserVerify]);

    const resetToDefault = useCallback(() => {
        setStatusMessage(null);
        setSelectBankIdMethodTabData({
            affiliatedBankId: undefined,
            bic: "",
            finaBankId: 0,
            socialSecurityNumber: undefined,
        });
        setDecoupledOtherDeviceTabData({ token: "", qrCodeImageUrl: "" });
        setDecoupledSameDeviceTabData({ bankIdApplicationUrl: "", token: "" });
        setRedirectTabData({
            bic: "",
            callbackActivationCode: "",
            callbackToken: "",
            finaBankId: 0,
            isCallback: false,
            socialSecurityNumber: undefined,
        });
        setStep(Step.SelectBank);
    }, []);

    const handleCancel = () => {
        onCancel();
        resetToDefault();
    };

    const handleError = useCallback(
        (
            startOver?: boolean,
            message?: PaymentInformationMessageResource | null,
        ) => {
            if (startOver) {
                resetToDefault();
            }
            setStatusMessage(message);
        },
        [resetToDefault],
    );

    const [statusMessage, setStatusMessage] = useState<
        PaymentInformationMessageResource | Nothing
    >();

    return (
        // @ts-ignore: TODO: Fix error
        <Sidebar
            className={styles.container}
            onClickOutside={handleCancel}
            ref={sidebarRef}
            show={visible}
        >
            <PayoutInformationStatusMessage
                bic={selectedBic}
                message={statusMessage}
            />
            <SelectBankTab
                onBankChanged={handleBankChanged}
                onBankSelected={handleBankSelected}
                onError={handleError}
                preselectedAffiliatedBankId={preselectedAffiliatedBankId}
                preselectedBic={preselectedBic}
                show={step === Step.SelectBank}
            />
            <SelectBankIdMethodTab
                data={selectBankIdMethodTabData}
                onBankIdMethodSelected={handleBankIdMethodSelected}
                onError={handleError}
                show={step === Step.SelectBankIdMethod}
            />
            <DecoupledOtherDeviceTab
                data={decoupledOtherDeviceTabData}
                onError={handleError}
                onReturn={handleChangeBankIdMethod}
                onUserVerify={handleUserVerified}
                show={step === Step.DecoupledOtherDevice}
            />
            <DecoupledSameDeviceTab
                data={decoupledSameDeviceTabData}
                onError={handleError}
                onReturn={handleChangeBankIdMethod}
                onUserVerify={handleUserVerified}
                show={step === Step.DecoupledSameDevice}
            />
            <RedirectTab
                data={redirectTabData}
                onError={handleError}
                onUserVerify={handleUserVerified}
                show={step === Step.Redirect}
            />
            <div className={styles.footerContainer}>
                <Button
                    text={t.cancel}
                    buttonType="button-secondary"
                    onClick={handleCancel}
                />
            </div>
        </Sidebar>
    );
};
